import React, { useMemo, useState } from "react";

import { useHealthScoreThresholds } from "../../../Hooks/useHealthScoreThresholds";
import useXFactorAccountBriefing from "../../../Hooks/useXFactorAccountBriefing";
import type {
  XFactorBand,
  XFactorSectionBubble,
} from "../../../Types/XFactorViewsRendering";
import SectionDetailModal from "../../TempViewsRendering/components/SectionDetailModal";
import {
  BAND_FILL,
  SECTION_COLUMN_LABEL,
  bandOf,
  formatDate,
  getSummary,
  headlineScores,
  pickText,
  presentSectionIds,
  renderSectionBubble,
  getSection,
} from "../../TempViewsRendering/lib/derive";

/** Same track chrome as Always On Business Health / ScoreBubble (section dots). */
const TRACK = 30;
const TRACK_FILL_MAX = 24;
const RELEVANCE_FILL: Record<XFactorBand, number> = {
  strong: 1,
  moderate: 0.5,
  weak: 0.25,
  unknown: 0.25,
};

/** Account Health Overall size — solid disc with score overlay. */
const OVERALL_SIZE = 48;

/**
 * The xfactor section row for one account, standing in for the IB briefing.
 *
 * Same derivation as the Temp Public Data bubble matrix — colour is section
 * health, diameter is relevance — laid out as this page's existing dot rail
 * rather than a matrix row, so it reads as part of the section it replaces.
 *
 * Hover uses the native tooltip: "Label — health N".
 */
const XFactorSectionDots: React.FC<{
  accountId: string;
  accountName?: string | null;
  /** Rendered when this account has no xfactor briefing either. */
  fallback: React.ReactNode;
}> = ({ accountId, accountName, fallback }) => {
  const { latest, data, loading } = useXFactorAccountBriefing(accountId);
  const { businessHealth: bhThresholds } = useHealthScoreThresholds();
  const [sectionId, setSectionId] = useState<string | null>(null);

  const bubbles = useMemo((): XFactorSectionBubble[] => {
    if (!data) return [];
    const scores = headlineScores(data);
    // Same source as My Portfolio Business Health (`extract_business_health_score`
    // / headlineScores().health). Prefer health over overall so Synopsis matches
    // the list (overall can differ by ~1pt on the same digest).
    const overallScore = scores.health ?? scores.overall;
    const overall: XFactorSectionBubble = {
      sectionId: "summary",
      label: SECTION_COLUMN_LABEL.summary,
      score: overallScore,
      band: bandOf(overallScore, bhThresholds),
      size: scores.fit == null ? 0.35 : 0.35 + (scores.fit / 100) * 0.65,
      relevance: scores.fit,
      synopsis: pickText(
        (getSummary(data) as { sectionSummary?: unknown } | null)
          ?.sectionSummary,
        ["synopsis"]
      ),
      riskCount: 0,
      opportunityCount: 0,
      risk: scores.risk,
      opportunity: scores.opportunity,
      confidence: null,
      missing: overallScore == null,
    };
    return [
      overall,
      ...presentSectionIds(data).map(id => {
        const bubble = renderSectionBubble(
          id,
          getSection(data, id),
          SECTION_COLUMN_LABEL[id] || id
        );
        return {
          ...bubble,
          band: bandOf(bubble.score, bhThresholds),
        };
      }),
    ];
  }, [data, bhThresholds]);

  if (loading) {
    return (
      <p className="m-0 text-sm text-gray-400">Loading Business Health…</p>
    );
  }

  // No xfactor briefing for this account — defer to the section's own message
  // rather than inventing a second empty state.
  if (!latest || !bubbles.length) return <>{fallback}</>;

  const name = latest.account_name || accountName || "Unknown account";

  return (
    <>
      <div className="mb-4 flex items-start gap-4">
        <div className="relative flex min-w-0 flex-1 flex-wrap items-start justify-between gap-y-3">
          {/* Connector line through bubble centres. */}
          {bubbles.length > 1 ? (
            <div
              className="pointer-events-none absolute top-[24px] hidden h-px bg-gray-200 sm:block"
              style={{
                left: `calc(100% / ${bubbles.length * 2})`,
                right: `calc(100% / ${bubbles.length * 2})`,
              }}
              aria-hidden
            />
          ) : null}

          {bubbles.map(bubble => {
            const isOverall = bubble.sectionId === "summary";
            const healthBand = bandOf(bubble.score, bhThresholds);
            const scoreText =
              bubble.missing || bubble.score == null
                ? "0"
                : String(Math.round(bubble.score));
            const relevanceBand = bandOf(bubble.relevance);
            const fill = Math.round(
              TRACK_FILL_MAX * RELEVANCE_FILL[relevanceBand]
            );
            const title =
              bubble.score == null
                ? `${bubble.label} — not scored`
                : `${bubble.label} — health ${Math.round(bubble.score)}`;

            return (
              <button
                key={bubble.sectionId}
                type="button"
                onClick={() => setSectionId(bubble.sectionId)}
                title={title}
                aria-label={`Open ${bubble.label} details`}
                className={`relative z-[1] flex flex-col items-center gap-1 border-0 bg-transparent p-0 ${
                  isOverall
                    ? "min-w-[5.5rem] cursor-pointer"
                    : "min-w-[4.5rem] flex-1 cursor-pointer"
                }`}
              >
                {isOverall ? (
                  /* Account Health Overall: solid disc + score overlay */
                  <span className="relative flex h-12 items-center justify-center bg-white">
                    <span
                      className="relative flex items-center justify-center rounded-full transition-transform hover:scale-110"
                      style={{
                        width: OVERALL_SIZE,
                        height: OVERALL_SIZE,
                        backgroundColor: bubble.missing
                          ? "rgba(100, 116, 139, 0.2)"
                          : BAND_FILL[healthBand],
                      }}
                    >
                      <span
                        className={`absolute z-10 text-xs font-semibold ${
                          bubble.missing || bubble.score === 0
                            ? "text-slate-500"
                            : "text-white"
                        }`}
                      >
                        {scoreText}
                      </span>
                    </span>
                  </span>
                ) : (
                  /* Section dots: grey outer track + coloured fill */
                  <span className="flex h-10 items-center justify-center bg-white">
                    <span
                      className="relative z-[1] flex items-center justify-center rounded-full transition-transform hover:scale-110"
                      style={{
                        width: TRACK,
                        height: TRACK,
                        backgroundColor: "#fff",
                        border: bubble.missing
                          ? "1.5px dashed #cbd5e1"
                          : "none",
                      }}
                    >
                      {bubble.missing ? null : (
                        <>
                          <span
                            aria-hidden
                            className="absolute inset-0 rounded-full"
                            style={{
                              backgroundColor: "rgba(156, 163, 175, 0.3)",
                            }}
                          />
                          <span
                            className="relative block rounded-full"
                            style={{
                              width: fill,
                              height: fill,
                              backgroundColor: BAND_FILL[healthBand],
                            }}
                          />
                        </>
                      )}
                    </span>
                  </span>
                )}

                {isOverall ? (
                  <span className="text-sm font-semibold text-gray-800">
                    Overall
                  </span>
                ) : (
                  <>
                    <span className="text-sm font-semibold tabular-nums text-gray-800">
                      {scoreText}
                    </span>
                    <span className="w-full text-center text-xs font-medium leading-snug text-gray-500">
                      {bubble.label}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-gray-400">
        {formatDate(latest.digest_date) || latest.digest_date} · via Temp Public
        Data briefing
      </div>

      <SectionDetailModal
        open={sectionId != null}
        onClose={() => setSectionId(null)}
        accountName={name}
        digestDate={latest.digest_date}
        data={data}
        sectionId={sectionId || "summary"}
        onNavigate={setSectionId}
      />
    </>
  );
};

export default XFactorSectionDots;
